import styled from 'styled-components';

import { ProductArrangementsToUpdate, ProductReplacementPopupInfo } from './types';
import { GetMenuProductArrangementsQuery, ProductState } from 'data/__generated__';
import { TotemImage } from 'components/TotemImage';
import { DetailLink } from 'components/DetailsView/DetailLink';
export const MenuProductArrangement = ({
    productArrangementId,
    productArrangementName,
    productArrangementIndex,
    rotations,
    productArrangementsToUpdate,
    setProductReplacementPopupInfo,
}: {
    productArrangementId: string;
    productArrangementName: string;
    productArrangementIndex: number;
    rotations: GetMenuProductArrangementsQuery['menuProductArrangements'][0]['productArrangementIndexes'][0]['rotations'];
    productArrangementsToUpdate: ProductArrangementsToUpdate;
    setProductReplacementPopupInfo: (arg: ProductReplacementPopupInfo) => void;
}) => {
    const nbLocationsTotal = rotations?.[0]?.products?.reduce((acc, { nbLocations }) => acc + nbLocations, 0) || 0;
    const nbUnitsTotal =
        rotations?.reduce((acc, { products }) => acc + products.reduce((acc, { quantity }) => acc + quantity, 0), 0) ||
        0;
    const refInfo = nbLocationsTotal !== 0 ? ` (${nbLocationsTotal} emplacements et ${nbUnitsTotal} unités)` : '';
    const linkName = `${productArrangementName}${refInfo}`;
    return (
        <ArrangementColumn key={`${productArrangementId}-${productArrangementIndex}`}>
            <ArrangementColumnTitle>
                <DetailLink path="product-arrangement" name={linkName} value={productArrangementId} color="#000" />
            </ArrangementColumnTitle>
            {rotations.map(({ rotationNumber, products }) => {
                const replacementProduct =
                    productArrangementsToUpdate?.[productArrangementId]?.[productArrangementIndex]?.[rotationNumber];

                const chosenProduct =
                    replacementProduct === null
                        ? null
                        : replacementProduct
                          ? replacementProduct.newProduct
                          : products?.[0]?.product;

                const nbReference = replacementProduct
                    ? 1
                    : Array.from(new Set(products.map(({ product }) => product._id))).length;

                const nbLocations = products?.reduce((acc, { nbLocations }) => acc + nbLocations, 0) || 0;
                const nbUnits = products?.reduce((acc, { quantity }) => acc + quantity, 0) || 0;

                // Check if product is not in the product arrangement (gamme)
                // If there's a replacement, use its isInProductArrangement value, otherwise use the original product's value
                const isNotInProductArrangement = replacementProduct
                    ? replacementProduct.isInProductArrangement === false
                    : products?.[0]?.isInProductArrangement === false;

                return (
                    <ProductForDay
                        key={`${productArrangementId}-${productArrangementIndex}-${rotationNumber}`}
                        onClick={() => {
                            setProductReplacementPopupInfo({
                                productArrangementId,
                                productArrangementName,
                                productArrangementIndex,
                                products,
                                rotationNumber,
                            });
                        }}
                        $replaced={!!replacementProduct}
                    >
                        {rotationNumber < 0 && <RotationTag>No R.</RotationTag>}
                        {rotationNumber > 0 && <RotationTag>R{rotationNumber}</RotationTag>}
                        {isNotInProductArrangement && <NotInRangeTag>Pas dans la gamme</NotInRangeTag>}
                        <TotemImage src={chosenProduct?.imageUrl || ''} alt="" size={50} />
                        <ProductInfo>
                            <NameField $nbReference={nbReference}>
                                {chosenProduct?.fullname || 'Produit retiré'}{' '}
                                {nbReference > 1 ? `(${nbReference} réf)` : ''}
                                <LocationInfo>
                                    {nbLocations} emplacements et {nbUnits} unités
                                </LocationInfo>
                                {chosenProduct && chosenProduct.state !== ProductState.Regular && (
                                    <ErrorMessage>État: {chosenProduct.state}</ErrorMessage>
                                )}
                                {chosenProduct && !chosenProduct.isMicrostore && (
                                    <ErrorMessage>Produit non disponible dans les microstores</ErrorMessage>
                                )}
                                {chosenProduct && !chosenProduct.imageUrl && (
                                    <ErrorMessage>Produit sans image</ErrorMessage>
                                )}
                                {chosenProduct && chosenProduct.isWithoutStock && (
                                    <ErrorMessage>Produit sans stock</ErrorMessage>
                                )}
                                {chosenProduct && chosenProduct.isToBeArchived && (
                                    <ErrorMessage>Produit à sortir</ErrorMessage>
                                )}
                            </NameField>
                        </ProductInfo>
                    </ProductForDay>
                );
            })}
        </ArrangementColumn>
    );
};

const ArrangementColumn = styled.div`
    cursor: pointer;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    flex-basis: 0;
    flex-grow: 1;
    margin: 2px;
    border: 1px solid ${({ theme }) => theme.darkBorderColor};

    & > :not(:first-child) {
        border-top: 1px solid ${({ theme }) => theme.darkBorderColor};
    }
`;

const ProductInfo = styled.div`
    display: flex;
    flex-basis: 0;
    flex-grow: 1;
    flex-direction: column;
`;

const ArrangementColumnTitle = styled.div`
    font-weight: bold;
    text-align: center;
    border-bottom: 1px solid ${({ theme }) => theme.darkBorderColor};
    padding: 4px 0;
`;

const ProductForDay = styled.div<{ $replaced: boolean }>`
    display: flex;
    flex-basis: 0;
    flex-grow: 1;
    width: 100%;
    padding: 3px;
    background-color: ${({ $replaced, theme }) => ($replaced ? theme.warningColor : theme.backgroundColor)};
    position: relative;

    &:hover {
        background-color: ${({ theme }) => theme.overlayColor};
        color: ${({ theme }) => theme.overlayTextColor};
    }
`;

const RotationTag = styled.div`
    position: absolute;
    top: 3px;
    right: 3px;
    background-color: ${({ theme }) => theme.menuBackgroundColor};
    color: ${({ theme }) => theme.menuTextColor};
    padding: 2px 6px;
    border-radius: 4px;
    font-size: 11px;
    font-weight: bold;
`;

const NotInRangeTag = styled.div`
    position: absolute;
    bottom: 3px;
    right: 3px;
    background-color: #d31500;
    color: white;
    padding: 2px 6px;
    border-radius: 4px;
    font-size: 10px;
    font-weight: bold;
`;

const NameField = styled.div<{ $nbReference: number }>`
    display: flex;
    flex: 1;
    flex-direction: column;
    font-weight: bold;
    color: ${({ $nbReference }) => ($nbReference <= 1 ? '#00b54b' : '#d31500')};
`;

const LocationInfo = styled.div`
    color: #000;
    font-size: 12px;
    font-weight: normal;
    margin-top: 2px;
`;

const ErrorMessage = styled.div`
    color: #d31500;
    font-size: 12px;
    margin-top: 2px;
`;
